
// ** Third Party Component
import axios from "axios"
import * as XLSX from "xlsx"
import { Task } from "gantt-task-react"
import { twMerge } from "tailwind-merge"
import { DateRange } from "react-day-picker"
import { type ClassValue, clsx } from "clsx"
import { RowSelectionState } from "@tanstack/react-table"
import { QuillDeltaToHtmlConverter } from "quill-delta-to-html"
import {
  Document,
  DocumentCode,
  DocumentText,
  Folder2,
  Image as ImageIcon,
  Music,
  VideoPlay,
} from "iconsax-reactjs"

// ** Page Routes
import { USERS_PAGE_ROUTE } from "./page-routes"

// ** Type
import { BillStatusInfo, DictType, DrillDownState } from "@pma/types"

// ** constants
import { AUDIO_EXTENSIONS, CHART_PALETTE, IMAGE_EXTENSIONS, VIDEO_EXTENSIONS } from "./constants"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const isValidUrl = (url: string) => {
  try {
    new URL(url)
    return true
  } catch (error) {
    return false
  }
}

const handleListItem = (arr: any, listTag: any, closeTag = false): string => {
  let dataString = listTag
  for (let index = 0; index < arr.length; index++) {
    const element = arr[index].item.ops[0].insert.value
    const listType = arr[0].item.op.attributes.list
    const innerlist = arr[index].innerList

    if (typeof element === "string") {
      dataString += `<li>${element}</li>`
    } else if (element?.denotationChar) {
      dataString += `<li><a class="user-name-link" href=${USERS_PAGE_ROUTE + "/" + element.id}>@${element.value}</a></li>`
    }

    if (innerlist !== null) {
      dataString += " &emsp; "
      return (dataString = handleListItem(innerlist.items, dataString))
    }
  }
  return dataString
}

export const parseMessage = (
  message: string,
  users: Array<DictType>,
  fmt?: "string" | "link",
): string => {
  if (users?.length < 1 && !message) {
    return ""
  }
  const usersById = users?.reduce(
    (acc: DictType, user: DictType) => {
      acc[user.id] = user
      return acc
    },
    {} as Record<string, string>,
  )

  if (message?.includes("insert")) {
    let arr = []
    try {
      arr = JSON.parse(message)
      if (typeof arr === "string") {
        arr = JSON.parse(arr)
      }
    } catch (error) {
      console.log(error, "error")
    }

    if (arr && arr.length > 0) {
      const converter = new QuillDeltaToHtmlConverter(arr, {
        encodeHtml: true,
        inlineStyles: true,
      })

      converter.beforeRender(function (groupType, data: any) {
        let converted = ""

        if (groupType === "block") {
          data?.ops?.forEach((element: any) => {
            if (element.insert?.value !== "\n") {
              converted += `<pre class='code-block'>${element.insert?.value}</pre>`
            } else {
              converted += "<br>"
            }
          })
        } else if (groupType === "list") {
          if (data.items && data.items.length > 0) {
            let listTag: string = ""
            const listType = data.items[0].item.op.attributes.list

            if (listType === "bullet") {
              listTag += `<ul>`
            } else if (listType === "ordered") {
              listTag += `<ol>`
            }

            for (let index = 0; index < data.items.length; index++) {
              const ops = data.items[index].item.ops
              let liContent = ""

              for (const op of ops) {
                const insertValue = op.insert?.value ?? op.insert
                const attributes = op.attributes || {}
                const hyperLink = attributes?.link

                if (hyperLink && typeof insertValue === "string") {
                  liContent += `<a target="_blank" rel="noreferrer" class="link-color" href="${hyperLink}">${insertValue}</a>`
                } else if (typeof insertValue === "string") {
                  liContent += applyStyles(insertValue, attributes)
                } else if (insertValue?.denotationChar) {
                  liContent += `<a class="user-name-link" href="${USERS_PAGE_ROUTE}/${insertValue.id}">@${insertValue.value}</a>`
                }
              }

              listTag += `<li>${liContent}</li>`
            }

            if (listType === "bullet") {
              listTag += `</ul>`
            } else if (listType === "ordered") {
              listTag += `</ol>`
            }

            converted += listTag
          }
        } else if (data?.ops) {
          data?.ops?.forEach((element: any) => {
            const attributes = element.attributes || {}
            const insertValue = element.insert?.value ?? element.insert

            if (insertValue === "\n") {
              converted += "<br>"
              return
            }

            // Handle base64 image data stored as plain text (legacy notes)
            if (
              typeof insertValue === "string" &&
              /data:image\/[a-zA-Z+]+;base64,/.test(insertValue)
            ) {
              converted += insertValue.replace(
                /data:image\/[a-zA-Z+]+;base64,[A-Za-z0-9+/=]+/g,
                (match) => `<img src="${match}" />`,
              )
              return
            }

            if (typeof insertValue === "string" && insertValue.includes("\n")) {
              const textWithBreaks = insertValue.replace(/\n/g, "<br>")
              converted += applyStyles(textWithBreaks, attributes)
              return
            }

            if (element.insert?.type === "text" || typeof insertValue === "string") {
              const textContent = element.insert?.value ?? insertValue
              converted += applyStyles(textContent, attributes)
            } else if (element.insert?.type === "mention") {
              converted += `<a class="user-name-link" href="${USERS_PAGE_ROUTE}/${element.insert?.value.id}">@${element.insert?.value.value}</a>`
            } else if (element.insert?.type === "image") {
              converted += `<img src="${element.insert.value}" />`
            }
          })
        }

        return converted
      })

      return converter.convert()
    }
  }

  if (fmt === "string") {
    return message?.replace(
      /@\d+#/g,
      (match) => `@${usersById[match?.slice(1, -1)]?.name}  ` || match,
    )
  }

  const processed = message?.replace(/@\d+#/g, (match) => {
    const userId = match?.slice(1, -1)
    const user: any = userId && getUserFromId(userId, users)

    if (user?.organization_name) {
      return `<a class="user-name-link" href="${USERS_PAGE_ROUTE}/${user.id}">@${user.organization_name}</a>`
    }
    return match
  })

  const withBreaks = processed?.replace(/\n/g, "<br>") || ""

  // Handle base64 image data in plain text notes (legacy)
  if (withBreaks.includes("data:image/")) {
    return withBreaks.replace(
      /data:image\/[a-zA-Z+]+;base64,[A-Za-z0-9+/=]+/g,
      (match) => `<img src="${match}" />`,
    )
  }

  return withBreaks
}
// ✅ Helper function to apply inline styles
function applyStyles(text: string, attributes: Record<string, any>): string {
  if (!text) return ""

  let result = text
  let styles: string[] = []
  let wrapperTags: { open: string; close: string }[] = []

  // Build inline styles
  if (attributes.color) {
    styles.push(`color: ${attributes.color}`)
  }
  if (attributes.background) {
    styles.push(`background-color: ${attributes.background}`)
  }
  if (attributes.size) {
    styles.push(`font-size: ${attributes.size}`)
  }

  // Handle formatting tags
  if (attributes.bold) {
    wrapperTags.push({ open: "<strong>", close: "</strong>" })
  }
  if (attributes.italic) {
    wrapperTags.push({ open: "<em>", close: "</em>" })
  }
  if (attributes.underline) {
    wrapperTags.push({ open: "<u>", close: "</u>" })
  }
  if (attributes.strike) {
    wrapperTags.push({ open: "<s>", close: "</s>" })
  }

  // Handle links
  if (attributes.link) {
    wrapperTags.push({
      open: `<a target="_blank" rel="noreferrer" class="link-color" href="${attributes.link}">`,
      close: "</a>",
    })
  }

  // Apply span with inline styles if any
  if (styles.length > 0) {
    result = `<span style="${styles.join("; ")}">${result}</span>`
  }

  // Wrap with formatting tags
  wrapperTags.forEach((tag) => {
    result = `${tag.open}${result}${tag.close}`
  })

  return result
}

export const getUserFromId = (userId: string, users: DictType[]): DictType => {
  const user = users?.find((user: DictType) => user?.id?.toString() === userId?.toString())
  return user || {}
}

function calculateTaskProgress(startDate: string | number | Date, endDate: string | number | Date) {
  const start = new Date(startDate)
  const end = new Date(endDate)
  const now = new Date()
  const differenceMs = end.getTime() - start.getTime()
  const timeLapse = now.getTime() - start.getTime()

  if (now < start) {
    return 0
  }

  if (now > end) {
    return 100
  }
  const oneDayMs = 24 * 60 * 60 * 1000

  const durationInDays = Math.floor(differenceMs / oneDayMs)

  const timeLapseInDays = Math.floor(timeLapse / oneDayMs)

  const progress = Math.floor((timeLapseInDays / durationInDays) * 100)

  return progress
}

export const formatGanttTasks = (task: DictType[]): Task[] => {
  return task
    .filter((task) => task.start_at !== null && task.finish_to !== null)
    .map((task) => {
      const { id, title, start_at, finish_to, status, depends_on } = task
      let current = new Date().getTime()
      let progress = calculateTaskProgress(start_at, finish_to)

      return {
        id: String(id) as string,
        type: "task",
        name: title,
        start: new Date(start_at),
        end: new Date(finish_to),
        progress: progress, // for testing we need to figure out how to determine task progress
        dependencies: depends_on.length > 0 ? depends_on.map((x: number) => String(x)) : [],
      }
    })
}

export function formatStringWithLink(input: string) {
  const urlPattern = /(https?:\/\/[^\s]+)/g
  if (typeof input !== "undefined") {
    const formattedString = input?.replace(
      urlPattern,
      '<a href="$1" class="link" target="_blank">$1</a>',
    )

    return formattedString
  } else {
    return input
  }
}

export const formatTime = (
  timeInMilliseconds: any,
  { toNow, amPm, duration, locale }: DictType,
  translate?: (text: string) => any,
) => {
  const timeDiff = Date.now() - timeInMilliseconds
  let timeMilli = toNow ? timeDiff : timeInMilliseconds

  if (amPm) {
    const timestamp = timeInMilliseconds
    const date = new Date(timestamp)
    const hours = date.getHours()
    const minutes = date.getMinutes()

    // Format hours in 12-hour format
    const formattedHours = hours % 12 || 12

    // Determine AM/PM suffix
    const isAm = hours < 12 || hours === 24
    const amPmLabel = locale === "he" ? (isAm ? "לפנה״צ" : "אחה״צ") : isAm ? translate?.("AM") : translate?.("PM")

    // Format minutes to have two digits
    const formattedMinutes = minutes < 10 ? "0" + minutes : minutes

    return `${formattedHours}:${formattedMinutes} ${amPmLabel}`
  }
  if (duration) {
    timeMilli = timeInMilliseconds
  }
  const totalSeconds = Math.floor(timeMilli / 1000)
  const seconds = totalSeconds % 60
  const minutes = Math.floor(totalSeconds / 60) % 60
  const hours = Math.floor(totalSeconds / 3600)

  const fHours = hours === 0 ? "" : hours + "h"
  const fMinutes = minutes === 0 && !fHours ? "" : minutes + "m"
  const fSeconds = seconds === 0 && !fMinutes ? "" : seconds + "s"

  return `${fHours} ${fMinutes} ${fSeconds}`
}

export const formatDate = (timestamp: any, lng?: string, options?: DictType) => {
  const date = new Date(timestamp)
  const defaultOptions: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
    year: "numeric",
  }
  const optionsToUse: Intl.DateTimeFormatOptions = options || defaultOptions
  return date.toLocaleDateString(lng ? lng : undefined, optionsToUse)
}

export const formatDate_he = (timestamp: any, options?: DictType) => {
  const date = new Date(timestamp)
  const defaultOptions: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
    year: "numeric",
  }
  const optionsToUse: Intl.DateTimeFormatOptions = options || defaultOptions

  return date.toLocaleDateString("he-IL", optionsToUse)
}

export const GetAIURL = (url: string) => {
  return `${"https://rog-tech.io/pma_agent"}${url}`
}
export const GetBIMFullUrl = (url?: string) => {
  var base = "https://rog-tech.io/bimserver"
  if (url) {
    return base + url
  }
  return base
}

export function formatDateString(dateString: string) {
  const date = new Date(dateString)
  const day = date.getDate().toString().padStart(2, "0")
  const month = (date.getMonth() + 1).toString().padStart(2, "0") // getMonth() returns month from 0-11
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}
export function convertDateToTimestamp(dateString: string): number {
  let str = formatDateString(dateString)
  const parts = str.split("/")
  // Note: months are 0-based in JavaScript
  const date = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]))
  return date.getTime()
}

export function getSubFoldersCount(
  data: any,
  translate?: (key: string) => string,
): {
  total: number
  data: { [key: string]: number }
  dataStrArr: string[]
} {
  const obj: { [key: string]: number } = {}
  const level = 0
  function countSubFolders(data: any, level = 0) {
    const { sub } = data
    if (!sub?.length) {
      if (obj[level]) {
        obj[level] += 0
      } else {
        obj[level] = 0
      }
      return 0
    }
    level++
    sub.forEach((ele: any) => {
      const count = countSubFolders(ele, level)
      if (obj[level]) {
        obj[level] += count
      } else {
        obj[level] = count
      }
    })
    return sub.length
  }
  const count = countSubFolders(data, level)
  obj[level] = count
  let total = 0
  let dataStrArr = []
  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      const count = obj[key]
      total += count
      const t = translate || ((k: string) => k)
      dataStrArr.push(
        `${key === "0" ? t("Root folder") : t("Sub Folder Level") + " " + key} : ${count}`,
      )
    }
  }
  return { total, data: obj, dataStrArr }
}

export function convertFileSize(sizeInBytes: number) {
  const kilobytes = sizeInBytes / 1024
  const megaBytes = kilobytes / 1024
  const gigaBytes = megaBytes / 1024
  if (gigaBytes > 1) {
    return `${gigaBytes.toFixed(2)} GB`
  }
  if (megaBytes > 1) {
    return `${megaBytes.toFixed(2)} MB`
  }
  return `${kilobytes.toFixed(2)} KB`
}

let subTasksArr: DictType[] = []
let parentId: null | number = null
export const taskFilterFn = (row: any, columnId: string, filterValue: string) => {
  const task = row.original
  const filterTerms = String(filterValue).toLowerCase().split("-").filter(Boolean)

  return filterTerms.every((term) => searchTask(task, term, row))
}

export const groupSearchFn = (row: any, columnId: string, filterValue: string) => {
  const task = row.original
  const filterTerms = String(filterValue).toLowerCase().split("-").filter(Boolean)

  return filterTerms.map((term) => searchGroupFilterFn(task, term, row))
}

const searchGroupFilterFn = (task: DictType, filterValue: string, row?: any) => {
  const filterText = String(filterValue).toLowerCase()

  let startTime: number | undefined
  let endTime: number | undefined
  let trackerState

  if (filterText.includes("to")) {
    const dateRange = filterText.split("to")

    startTime = parseInt(dateRange[0])
    endTime = dateRange.length > 1 ? parseInt(dateRange[1]) : startTime
  }

  if (filterText === "running") {
    trackerState = true
  }

  // Search through main task fields
  if (
    task.title?.toLowerCase().includes(filterText) ||
    String(task?.id).includes(filterText) ||
    task?.description?.toLowerCase().includes(filterText) ||
    task?.status?.name?.toLowerCase().includes(filterText) ||
    task?.notes_id?.some((note: DictType) =>
      Object.values(note)
        .map((val) => String(val).toLowerCase())
        .some((value) => value.includes(filterText)),
    ) ||
    task.responsible_id?.some((user: DictType) =>
      Object.values(user)
        .map((val) => String(val).toLowerCase())
        .some((value) => value.includes(filterText)),
    ) ||
    (task?.created_at > startTime! && task?.created_at < endTime!) ||
    (trackerState && task.active_session_number)
  ) {
    if (task._subtasks && task._subtasks.length > 0) {
      subTasksArr = task._subtasks
      parentId = task.id

      if (row && row.getCanExpand() && !row.getIsExpanded()) {
        row.getToggleExpandedHandler()()
      }
    }
    return task
  }

  //  check in subtasks

  if (subTasksArr.length > 0 && task.parent_tasks_id) {
    const found = subTasksArr.find((val) => val.id === task.id && val.parent_tasks_id === parentId)

    if (found) {
      return task
    } else {
      return false
    }
  } else if (
    task._subtasks?.some((subtask: DictType) => searchGroupFilterFn(subtask, filterText))
  ) {
    return task
  }
  subTasksArr = []
  parentId = null
  return false
}

const searchTask = (task: DictType, filterValue: string, row?: any) => {
  const filterText = String(filterValue).toLowerCase()

  let startTime: number | undefined
  let endTime: number | undefined
  let trackerState

  if (filterText.includes("to")) {
    const dateRange = filterText.split("to")

    startTime = parseInt(dateRange[0])
    endTime = dateRange.length > 1 ? parseInt(dateRange[1]) : startTime
  }

  if (filterText === "running") {
    trackerState = true
  }

  // Search through main task fields
  if (
    task.title?.toLowerCase().includes(filterText) ||
    String(task?.id).includes(filterText) ||
    task?.description?.toLowerCase().includes(filterText) ||
    task?.status?.name?.toLowerCase().includes(filterText) ||
    task?.notes_id?.some((note: DictType) =>
      Object.values(note)
        .map((val) => String(val).toLowerCase())
        .some((value) => value.includes(filterText)),
    ) ||
    task.responsible_id?.some((user: DictType) =>
      Object.values(user)
        .map((val) => String(val).toLowerCase())
        .some((value) => value.includes(filterText)),
    ) ||
    (task?.created_at > startTime! && task?.created_at < endTime!) ||
    (trackerState && task.active_session_number)
  ) {
    if (task._subtasks && task._subtasks.length > 0) {
      subTasksArr = task._subtasks
      parentId = task.id

      if (row && row.getCanExpand() && !row.getIsExpanded()) {
        row.getToggleExpandedHandler()()
      }
    }
    return true
  }

  //  check in subtasks

  if (subTasksArr.length > 0 && task.parent_tasks_id) {
    const found = subTasksArr.find((val) => val.id === task.id && val.parent_tasks_id === parentId)

    if (found) {
      return true
    } else {
      return false
    }
  } else if (task._subtasks?.some((subtask: DictType) => searchTask(subtask, filterText))) {
    return true
  }
  subTasksArr = []
  parentId = null
  return false
}

export const projectFilterFn = (row: any, columnId: string, filterValue: string) => {
  const project = row.original
  const filterTerms = String(filterValue).toLowerCase().split("-").filter(Boolean)

  return filterTerms.every((term) => searchProject(project, term))
}
const searchProject = (project: DictType, filterValue: string) => {
  const filterText = String(filterValue).toLowerCase()

  if (
    project.title?.toLowerCase().includes(filterText) ||
    project.project_number?.toLowerCase().includes(filterText) ||
    project?.address_text?.toLowerCase().includes(filterText) ||
    project.type?.project_type?.toLowerCase().includes(filterText) ||
    project.type?.project_type_en?.toLowerCase().includes(filterText)
  ) {
    return true
  }

  return false
}

// ai

export function getCurrentTimeFormatted(locale: string) {
  const currentTime = new Date()
  const options = {
    hour: "numeric" as "numeric",
    minute: "numeric" as "numeric",
    hour12: true, // to display in 12-hour format with AM/PM
  }

  const formattedTime = currentTime.toLocaleTimeString("en-US", options)

  // Example RTL language handling (e.g., Hebrew)
  const language = "he-IL" // Hebrew (Israel) locale
  const formattedTimeRTL = currentTime.toLocaleTimeString(language, options)

  // Example format: Today 4:08 pm
  const todayFormatted = `Today ${formattedTime}`

  return locale === "en" ? todayFormatted : formattedTimeRTL
}

export const buildBoQHierarchicalData = (
  data: Omit<DictType, "level" | "hasChildren" | "parentCode">[],
): DictType[] => {
  const sortedData = [...data].sort((a, b) => {
    const aParts = a?.number.split(".")
    const bParts = b?.number.split(".")
    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      if (aParts[i] === undefined) return -1
      if (bParts[i] === undefined) return 1
      if (aParts[i] < bParts[i]) return -1
      if (aParts[i] > bParts[i]) return 1
    }
    return 0
  })

  const codeToNode: Record<string, { level: number; parentCode?: string; children: string[] }> = {}

  sortedData.forEach((item) => {
    const codeParts = item?.number.split(".")
    let parentCode: string | undefined =
      codeParts.length > 1 ? codeParts.slice(0, -1).join(".") : undefined

    let actualParentCode = parentCode
    while (actualParentCode && !sortedData.some((other) => other.number === actualParentCode)) {
      const parentParts = actualParentCode.split(".")
      if (parentParts.length === 1) {
        actualParentCode = undefined
        break
      }
      actualParentCode = parentParts.slice(0, -1).join(".")
    }

    const level = actualParentCode ? (codeToNode[actualParentCode]?.level ?? 0) + 1 : 0

    codeToNode[item.number] = { level, parentCode: actualParentCode, children: [] }

    if (actualParentCode && codeToNode[actualParentCode]) {
      codeToNode[actualParentCode].children.push(item.number)
    }
  })

  return sortedData.map((item) => {
    const node = codeToNode[item.number]
    return {
      ...item,
      level: node.level,
      parentCode: node.parentCode,
      hasChildren: node.children.length > 0,
    }
  })
}

export function formatPercent(value: number): string {
  if (!Number.isFinite(value)) return "0%"
  if (Number.isInteger(value)) return value + "%"
  return value.toFixed(1).replace(/\.0$/, "") + "%"
}

/**
 * Generic Excel export for any array of objects.
 * @param rows - Array of plain objects (each key becomes a column header).
 * @param fileName - Name for the downloaded file (without .xlsx extension).
 */
export function exportTableToExcel(rows: Record<string, any>[], fileName: string) {
  if (!rows.length) return
  const ws = XLSX.utils.json_to_sheet(rows)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1")
  XLSX.writeFile(wb, `${fileName}.xlsx`)
}

export const exportToExcel = (
  data: DictType[],
  table: "estimate" | "main" | "bills" | "bill-preview",
  selectedRows?: RowSelectionState,
  isHebrew = true,
) => {
  let dataToExport: DictType[]

  if (selectedRows && Object.keys(selectedRows).length > 0) {
    const selectedIds = new Set<string>()

    Object.keys(selectedRows).forEach((id) => {
      if (selectedRows[id]) {
        selectedIds.add(id)
      }
    })

    data.forEach((item) => {
      if (selectedIds.has(item.id)) {
        data.forEach((child) => {
          if (child.number.startsWith(item.number + ".")) {
            selectedIds.add(child.id)
          }
        })
      }
    })

    dataToExport = data.filter((item) => selectedIds.has(item.id))
  } else {
    dataToExport = data
  }

  dataToExport.sort((a, b) => a?.number?.localeCompare(b.number))

  const estimateExcelData = dataToExport.map((item) => ({
    Number: item.number,
    Description: "  ".repeat(item?.level) + item.description,
    Unit: item?.units,
    Amount: item?.amount,
    Price: item.price || "",
    Sum: item.sum,
  }))

  const mainKeys = isHebrew
    ? [
      "סכום מצטבר בפרויקט",
      "סכום כתב כמויות",
      "כמות מצטברת בפרויקט",
      "הנחה",
      "מחיר ליחידה",
      "כמות מכתב הכמויות",
      "יחידה",
      "תיאור",
      "סעיף",
    ]
    : [
      "Section",
      "Description",
      "Unit",
      "Amount From estimate",
      "Price per unit",
      "Discount (%)",
      "Accumulated Amount",
      "Boq Sum",
      "Accumulated Sum",
    ]

  const mainExcelData = dataToExport.map((item) => {
    const enValues = [
      item?.number,
      "  ".repeat(item?.level) + item?.description,
      item?.units,
      item?.amount,
      item?.boq_cost || 0,
      `${(item?.boq_cost - item?.discount_price)?.toFixed(2)} (${(item?.total_discount ?? 0) * 100}%)` ||
      0,
      item?.paid || 0,
      item?.real_sum,
      item?.discount_price * (item?.paid || 0),
    ]
    const heValues = [
      item?.discount_price * (item?.paid || 0),
      item?.real_sum,
      item?.paid || 0,
      `${(item?.boq_cost - item?.discount_price)?.toFixed(2)} (${(item?.total_discount ?? 0) * 100}%)` ||
      0,
      item?.boq_cost || 0,
      item?.amount,
      item?.units,
      "  ".repeat(item?.level) + item?.description,
      item?.number,
    ]

    return mainKeys.reduce((acc: DictType, key, idx) => {
      acc[key] = isHebrew ? heValues[idx] : enValues[idx]
      return acc
    }, {})
  })

  const billKeys = isHebrew
    ? [
      "סכום כתב כמויות",
      "סה'כ נוכחי מאושר",
      "סה'כ נוכחי ביקש",
      "הצעת החוק הנוכחית אושרה",
      "הצעת החוק הנוכחית המבוקשת",
      "מחיר ליחידה",
      "הנחה",
      "הערכה מחיר",
      "כמות מצטברת בפרויקט",
      "כמות מכתב הכמויות",
      "יחידה",
      "תיאור",
      "סעיף",
    ]
    : [
      "Section",
      "Description",
      "Unit",
      "Amount From estimate",
      "Accumulated Amount",
      "Estimate Price",
      "Discount (%)",
      "Price per unit",
      "Requested Current Bill",
      "Approved Current Bill",
      "Requested Current Total",
      "Approved Current Total",
      "Boq Sum",
    ]

  const billExcelData = dataToExport.map((item) => {
    let values: any[] = []
    const enValues = [
      item?._boq_item?.number,
      item?._boq_item?.description,
      item?._boq_item?.units,
      item?._boq_item?.amount,
      item?._boq_item?.paid || 0,
      item?._boq_item?.boq_cost || 0,
      `${(item?._boq_item?.boq_cost - item?.discount_price)?.toFixed(2)} (${((item?.item_discount ?? 0) * 100).toFixed(2)})` ||
      0,
      `${item?.discount_price?.toFixed(2) || 0} (Exclude Discount: ${item?.item_discount === 0 ? "true" : "false"})`,
      item?.amount,
      item?.approved_amount,
      item?.requested_sum,
      item?.approved_sum,
      item?._boq_item?.real_sum,
    ]
    values = [
      item?._boq_item?.real_sum,
      item?.approved_sum,
      item?.requested_sum,
      item?.approved_amount,
      item?.amount,
      `${item?.discount_price?.toFixed(2) || 0} (אל תכלול הנחה: ${item?.item_discount === 0 ? "נָכוֹן" : "שֶׁקֶר"})`,
      `${(item?._boq_item?.boq_cost - item?.discount_price)?.toFixed(2)} (${((item?.item_discount ?? 0) * 100).toFixed(2)})` ||
      0,
      item?._boq_item?.boq_cost || 0,
      item?._boq_item?.paid || 0,
      item?._boq_item?.amount,
      item?._boq_item?.units,
      item?._boq_item?.description,
      item?._boq_item?.number,
    ]

    return billKeys.reduce((acc: DictType, key, idx) => {
      if (isHebrew) {
        acc[key] = values[idx]
      } else {
        acc[key] = enValues[idx]
      }
      return acc
    }, {})
  })

  const billPreviewKeys = isHebrew
    ? [
      "סכום מצטבר בפרויקט",
      "סכום כתב כמויות",
      "כמות מצטברת בפרויקט",
      "כמות מכתב הכמויות",
      "מחיר ליחידה",
      "יחידה",
      "תיאור",
      "סעיף",
    ]
    : [
      "Section",
      "Description",
      "Unit",
      "Price per unit",
      "Amount From estimate",
      "Accumulated Amount",
      "Boq Sum",
      "Accumulated Sum",
    ]

  const billPreviewExcelData = dataToExport.map((item) => {
    const bill = item?.bill
    let accumulatedAmount = 0
    let accumulatedSum = 0

    if (bill) {
      if (["new", "pending"].includes(bill.fullBillStatus) && item?.is_item) {
        accumulatedAmount = (bill?.amount || 0) + (bill?._boq_item?.paid || 0)
        accumulatedSum = (bill?.requested_sum || 0) + (bill?._boq_item?.paid_sum || 0)
      } else if (["approved", "signed"].includes(bill.fullBillStatus) && item?.is_item) {
        accumulatedAmount = (bill?.approved_amount || 0) + (bill?._boq_item?.paid || 0)
        accumulatedSum = (bill?.approved_sum || 0) + (bill?._boq_item?.paid_sum || 0)
      }
    }

    const enValues = [
      item?.number,
      "  ".repeat(item?.level) + item?.description,
      item?.units,
      item?.discount_price || 0,
      item?.amount,
      accumulatedAmount,
      item?.real_sum,
      accumulatedSum,
    ]
    const heValues = [
      accumulatedSum,
      item?.real_sum,
      accumulatedAmount,
      item?.amount,
      item?.discount_price || 0,
      item?.units,
      "  ".repeat(item?.level) + item?.description,
      item?.number,
    ]

    return billPreviewKeys.reduce((acc: DictType, key, idx) => {
      acc[key] = isHebrew ? heValues[idx] : enValues[idx]
      return acc
    }, {})
  })

  const excelData =
    table === "estimate"
      ? estimateExcelData
      : table === "bills"
        ? billExcelData
        : table === "bill-preview"
          ? billPreviewExcelData
          : mainExcelData

  const wb = XLSX.utils.book_new()
  const ws = XLSX.utils.json_to_sheet(excelData)

  const colWidths = [
    { wch: 15 },
    { wch: isHebrew ? 10 : 40 },
    { wch: 10 },
    { wch: 10 },
    { wch: 15 },
    { wch: 20 },
    { wch: 15 },
    { wch: 10 },
    { wch: 10 },
    { wch: 20 },
    { wch: isHebrew ? 40 : 10 },
    { wch: 15 },
  ]
  ws["!cols"] = colWidths
  ws["!dir"] = isHebrew ? "rtl" : "ltr"

  if (table === "bills") {
    XLSX.utils.book_append_sheet(wb, ws, isHebrew ? "פריטי חשבונית" : "Bill Items")
  } else if (table === "bill-preview") {
    XLSX.utils.book_append_sheet(wb, ws, isHebrew ? "תצוגה מקדימה של חשבון" : "Bill Preview")
  } else {
    XLSX.utils.book_append_sheet(wb, ws, isHebrew ? "פריטי בוק" : "Boq Items")
  }

  const firstBillItem = data[0]
  const billCreatedAt = firstBillItem?.created_at ? new Date(firstBillItem.created_at) : new Date()
  const dateStr =
    table === "bills" || table === "bill-preview"
      ? billCreatedAt.toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0]
  const selectedSuffix =
    selectedRows && Object.keys(selectedRows).length > 0 ? `-${"selected"}` : ""
  const tableLabel = table === "bills" ? "bills" : table === "bill-preview" ? "bill-preview" : "boq"
  const filename = `${tableLabel}-${data.length}-${isHebrew ? "פריטים" : "items"}${selectedSuffix}-${dateStr}.xlsx`

  try {
    XLSX.writeFile(wb, filename, { bookType: "xlsx", type: "binary" })
  } catch (error) {
    console.error("Export error:", error)

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" })
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    })

    const url = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }
}

export const getAllItemChildrenIds = (
  selectedRows: RowSelectionState,
  hierarchicalData: DictType[],
): Set<string> => {
  const allSelectedIds = new Set<string>()

  Object.keys(selectedRows).forEach((id) => {
    if (selectedRows[id]) {
      allSelectedIds.add(id)
    }
  })

  hierarchicalData.forEach((item) => {
    if (allSelectedIds.has(item.id)) {
      hierarchicalData.forEach((child) => {
        if (child?.number.startsWith(item?.number + ".")) {
          allSelectedIds.add(child.id)
        }
      })
    }
  })

  return allSelectedIds
}

export const computeBillTotal = (bill: DictType, contractor?: boolean) => {
  let total = 0
  bill?._bill_items?.forEach((item: DictType) => {
    if (item?._boq_item?.is_item) {
      total += Number(item?.requested_sum) || 0
    }
  })
  return total
}

/**
 * Get all signed bills that were signed BEFORE the given bill (chronologically).
 * For unsigned bills: ALL signed bills count as "previous".
 * Uses signed_at timestamp, NOT bill ID or paid_sum.
 */
const getPreviousSignedBills = (bill: DictType, allBillsOnBoq: DictType[]) => {
  if (!allBillsOnBoq?.length) return []
  const billSignedAt = Number(bill?.signed_at) || 0

  return allBillsOnBoq.filter((b: DictType) => {
    if (!b?.is_signed || b?.deleted) return false
    if (b?.id === bill?.id) return false
    if (!bill?.is_signed) return true // unsigned: all signed bills are "previous"
    return (Number(b?.signed_at) || 0) < billSignedAt
  })
}

/**
 * Bill-level accumulated from previous signed bills.
 * Sum of computeBillTotal for all bills signed before this one.
 * Does NOT use paid_sum — computes from actual bill item data + signed_at order.
 */
export const getPreviouslyPaidTotal = (bill: DictType, allBillsOnBoq?: DictType[]) => {
  if (!allBillsOnBoq?.length) {
    // Fallback: use paid_sum (old behavior) when allBillsOnBoq not available
    const items = bill?._bill_items || []
    return items
      .filter((item: DictType) => item?._boq_item?.is_item !== false)
      .reduce((sum: number, item: DictType) => sum + (Number(item?._boq_item?.paid_sum) || 0), 0)
  }
  return getPreviousSignedBills(bill, allBillsOnBoq).reduce(
    (sum: number, b: DictType) => sum + computeBillTotal(b, false),
    0,
  )
}

/**
 * Per-item accumulated from previous signed bills.
 * For a specific BOQ item, sum approved_sum from bills signed before this one.
 */
export const getItemPreviouslyPaid = (
  boqItemId: number,
  bill: DictType,
  allBillsOnBoq: DictType[],
) => {
  if (!allBillsOnBoq?.length || !boqItemId) return 0
  let total = 0
  for (const b of getPreviousSignedBills(bill, allBillsOnBoq)) {
    const match = (b?._bill_items || []).find(
      (item: DictType) => item?.boq_item_id === boqItemId && !item?.declined,
    )
    if (match) total += Number(match?.approved_sum) || 0
  }
  return total
}

/**
 * Total accumulated including the current bill.
 * Previous bills (by signed_at) + current bill's approved total.
 */
export const getAccumulatedTotal = (bill: DictType, allBillsOnBoq?: DictType[]) => {
  return (Number(getPreviouslyPaidTotal(bill, allBillsOnBoq)) || 0) + (Number(computeBillTotal(bill, false)) || 0)
}

/**
 * Returns unsigned, non-deleted bills on the same BOQ that were created before the given bill.
 * Used to warn when signing out of order.
 */
export const getUnsignedEarlierBills = (bill: DictType, allBills: DictType[]) => {
  if (!bill?.id || !allBills?.length) return []
  return allBills.filter(
    (b: DictType) => b?.id < bill.id && !b?.is_signed && !b?.deleted && b?.status !== "declined",
  )
}

export const renderTime = (date: any, translate: (key: string) => string) => {
  const timestamp = date
  const now = Date.now()

  const diffInMs = now - timestamp
  const diffInSeconds = Math.floor(diffInMs / 1000)
  const diffInMinutes = Math.floor(diffInSeconds / 60)
  const diffInHours = Math.floor(diffInMinutes / 60)
  const diffInDays = Math.floor(diffInHours / 24)

  let result = ""

  if (diffInDays === 0) {
    if (diffInHours === 0) {
      if (diffInMinutes === 0) {
        result = translate("Just now")
      } else {
        result = `${diffInMinutes} ${translate(diffInMinutes > 1 ? "minutes ago" : "minute ago")}`
      }
    } else {
      result = `${diffInHours} ${translate(diffInHours > 1 ? "hours ago" : "hour ago")}`
    }
  } else if (diffInDays > 0) {
    if (diffInDays >= 365) {
      const years = Math.floor(diffInDays / 365)
      result = `${years} ${translate(years > 1 ? "years from now" : "year from now")}`
    } else if (diffInDays >= 30) {
      const months = Math.floor(diffInDays / 30)
      result = `${months} ${translate(months > 1 ? "months from now" : "month from now")}`
    } else {
      result = `${diffInDays} ${translate(diffInDays > 1 ? "days from now" : "day from now")}`
    }
  } else {
    const absDiffInDays = Math.abs(diffInDays)

    if (absDiffInDays >= 365) {
      const years = Math.floor(absDiffInDays / 365)
      result = `${years} ${translate(years > 1 ? "years ago" : "year ago")}`
    } else if (absDiffInDays >= 30) {
      const months = Math.floor(absDiffInDays / 30)
      result = `${months} ${translate(months > 1 ? "months ago" : "month ago")}`
    } else {
      result = `${absDiffInDays} ${translate(absDiffInDays > 1 ? "days ago" : "day ago")}`
    }
  }

  return result
}

export function formatDateRange(startAt: number, finishTo: number) {
  const startDate = new Date(startAt)
  const endDate = new Date(finishTo)

  const dayStart = startDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
  })

  const dayEnd = endDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })

  return `${dayStart} - ${dayEnd}`
}

export function hexToRGBA(hex: string, alpha: number): string {
  let r = 0,
    g = 0,
    b = 0

  if (!hex) {
    return "white"
  }

  // Remove `#` if present
  hex = hex.replace("#", "")

  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16)
    g = parseInt(hex[1] + hex[1], 16)
    b = parseInt(hex[2] + hex[2], 16)
  } else if (hex.length === 6) {
    r = parseInt(hex.slice(0, 2), 16)
    g = parseInt(hex.slice(2, 4), 16)
    b = parseInt(hex.slice(4, 6), 16)
  }

  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export const showTimerInFormat = (seconds: number) => {
  const hrs = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  return `${hrs.toString().padStart(2, "0")}:${mins
    .toString()
    .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
}

export function formatMentions(input: string) {
  const mentionRegex = /\@\[([^\]]+)\]\((\d+)\)/g

  const formattedOutput = input.replace(mentionRegex, (match, name, id) => {
    return `@${id}#`
  })

  return formattedOutput
}

export function getContrastTextColor(hexColor: string): string {
  // Remove # if present
  const hex = hexColor.replace("#", "")

  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  // Calculate relative luminance using the formula
  // (perceived brightness based on human eye sensitivity)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

  // Return white for dark backgrounds, black for light backgrounds
  return luminance > 0.5 ? "#000000" : "#FFFFFF"
}

export const getFileExtension = (filename: string): string =>
  filename.slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2).toLowerCase()

export const getFileIconConfig = (file: File) => {
  const ext = getFileExtension(file.name)
  const type = file.type

  // By MIME type first
  if (type.startsWith("image/"))
    return { icon: ImageIcon, color: "text-green-500", bg: "bg-green-100" }
  if (type.startsWith("video/"))
    return { icon: VideoPlay, color: "text-purple-500", bg: "bg-purple-100" }
  if (type.startsWith("audio/")) return { icon: Music, color: "text-pink-500", bg: "bg-pink-100" }
  if (type === "application/pdf")
    return { icon: DocumentText, color: "text-red-500", bg: "bg-red-100" }

  // By extension
  const extensionMap: Record<string, { icon: typeof Document; color: string; bg: string }> = {
    doc: { icon: DocumentText, color: "text-blue-600", bg: "bg-blue-100" },
    docx: { icon: DocumentText, color: "text-blue-600", bg: "bg-blue-100" },
    xls: { icon: DocumentText, color: "text-green-600", bg: "bg-green-100" },
    xlsx: { icon: DocumentText, color: "text-green-600", bg: "bg-green-100" },
    ppt: { icon: DocumentText, color: "text-orange-500", bg: "bg-orange-100" },
    pptx: { icon: DocumentText, color: "text-orange-500", bg: "bg-orange-100" },
    txt: { icon: DocumentText, color: "text-neutral-600", bg: "bg-neutral-100" },
    json: { icon: DocumentCode, color: "text-yellow-600", bg: "bg-yellow-100" },
    xml: { icon: DocumentCode, color: "text-orange-600", bg: "bg-orange-100" },
    html: { icon: DocumentCode, color: "text-orange-500", bg: "bg-orange-100" },
    css: { icon: DocumentCode, color: "text-blue-500", bg: "bg-blue-100" },
    js: { icon: DocumentCode, color: "text-yellow-500", bg: "bg-yellow-100" },
    dwg: { icon: Document, color: "text-red-600", bg: "bg-red-100" },
    dxf: { icon: Document, color: "text-red-500", bg: "bg-red-100" },
    skn: { icon: Document, color: "text-indigo-500", bg: "bg-indigo-100" },
    tlv: { icon: Document, color: "text-cyan-500", bg: "bg-cyan-100" },
    shp: { icon: Document, color: "text-teal-500", bg: "bg-teal-100" },
    zip: { icon: Folder2, color: "text-yellow-600", bg: "bg-yellow-100" },
    rar: { icon: Folder2, color: "text-purple-600", bg: "bg-purple-100" },
  }

  return extensionMap[ext] || { icon: Document, color: "text-neutral-500", bg: "bg-neutral-200" }
}

export const generateId = () => Math.random().toString(36).substring(2, 9)

export const formatTimestampToDate = (timestamp: number) => {
  const date = new Date(timestamp)

  const day = String(date.getDate()).padStart(2, "0")
  const month = String(date.getMonth() + 1).padStart(2, "0") // Months are 0-based
  const year = String(date.getFullYear()).slice(-2) // last 2 digits

  return `${day}-${month}-${year}`
}

export const isFolder = (item: any) => {
  return item?.type === "folder" || item?._subfolders_count !== undefined
}

export const formatTimestamp = (timestamp: number, locale: string) => {
  const date = new Date(timestamp)
  const targetLocale = locale === "he" ? "he-IL" : locale || "en-US"
  return date.toLocaleDateString(targetLocale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

export const formatFolderSize = (size: number, translate?: (text: string) => string) => {
  const t = translate || ((s: string) => s)
  if (size === 0) {
    return `0 ${t("KB")}`
  }

  const kb = size / 1000
  if (kb < 1000) {
    return `${Math.round(kb)} ${t("KB")}`
  }

  const mb = kb / 1000
  if (mb < 1000) {
    return `${mb.toFixed(2)} ${t("MB")}`
  }

  const gb = mb / 1000
  return `${gb.toFixed(2)} ${t("GB")}`
}

export const formatTimeForVideoOrAudio = (seconds: number) => {
  // Handle NaN, Infinity, and undefined values
  if (!seconds || !isFinite(seconds) || isNaN(seconds)) {
    return "0:00"
  }
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, "0")}`
}

// Helper to check file type
export const isImageType = (type: string): boolean =>
  IMAGE_EXTENSIONS.some((t) => type?.toLowerCase()?.includes(t))

export const isVideoType = (type: string): boolean =>
  VIDEO_EXTENSIONS.some((t) => type?.toLowerCase()?.includes(t))

export const isAudioType = (type: string): boolean =>
  AUDIO_EXTENSIONS.some((t) => type?.toLowerCase()?.includes(t))

export const isCodeType = (type: string): boolean =>
  AUDIO_EXTENSIONS.some((t) => type?.toLowerCase()?.includes(t))

export const isPdfType = (type: string): boolean => type?.toLowerCase()?.includes("pdf")

export const getModifiedFileName = (originalName: string): string => {
  const dotIndex = originalName.lastIndexOf(".")
  if (dotIndex === -1) return originalName + " - Copy"

  const name = originalName.substring(0, dotIndex)
  const extension = originalName.substring(dotIndex)
  return `${name} - Copy${extension}`
}

export const fetchAndCreateFile = async (
  url: string,
  originalName: string,
  condition: boolean,
): Promise<File> => {
  const response = await fetch(url)
  const blob = await response.blob()
  const modifiedName = condition ? getModifiedFileName(originalName) : originalName
  return new window.File([blob], modifiedName, { type: blob.type })
}

/**
 * Determine the display file type from a file object.
 */
export function getFileDisplayType(file: DictType): string {
  const fileName = file?.name?.replace(/\.[^/.]+$/, "").toLowerCase()
  if (fileName === file?.type?.toLowerCase()) return "txt"
  if (file?.type === "") return "txt"
  return file?.type
}

/**
 * Download a file by creating a temporary link.
 */
export function downloadFile(file: any): void {
  axios({
    url: file?.file,
    method: "GET",
    responseType: "blob",
  }).then((response) => {
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement("a")
    link.href = url
    const fileName = file?.name || "downloaded_file"
    link.setAttribute("download", fileName)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  })
}

/**
 * Collapse all subfolders starting from a given folder ID.
 */
export function collapseSubfolders(
  data: { [id: number]: any[] },
  folderId: number,
  includeSelf: boolean = false,
): { [id: number]: any[] } {
  if (includeSelf) {
    for (const key in data) {
      data[key].forEach((item: any) => {
        if (item.as === "folder" && item.id === folderId) {
          item.isExpanded = false
        }
      })
    }
  }

  function recurse(currentId: number): void {
    const children = data[currentId]
    if (!children) return
    for (const item of children) {
      if (item.as === "folder") {
        if (item.isExpanded) item.isExpanded = false
        recurse(item.id)
      }
    }
  }

  recurse(folderId)
  return data
}

/**
 * Simple debounce utility.
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number,
): ((...args: Parameters<T>) => void) & { cancel: () => void } {
  let timeoutId: NodeJS.Timeout | null = null

  const debounced = function (this: any, ...args: Parameters<T>) {
    if (timeoutId) clearTimeout(timeoutId)
    timeoutId = setTimeout(() => {
      func.apply(this, args)
      timeoutId = null
    }, delay)
  }

  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
  }

  return debounced as ((...args: Parameters<T>) => void) & { cancel: () => void }
}

// ** File Upload Dialog Helpers
export const getFileNameWithoutExtension = (filename: string): string => {
  const lastDotIndex = filename.lastIndexOf(".")
  return lastDotIndex > 0 ? filename.slice(0, lastDotIndex) : filename
}

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 B"
  const k = 1024
  const sizes = ["B", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i]
}

export const showTotalAccumulatedSum = (bill: any) => {
  let totalAmount = 0
  const items = bill?._bill_items || []
  if (["new", "pending"].includes(bill?.status)) {
    items.forEach((item: any) => {
      if (item?._boq_item?.is_item) {
        totalAmount += Number(item?.requested_sum) || 0
      }
    })
  } else if (bill?.status === "approved") {
    items.forEach((item: any) => {
      if (item?._boq_item?.is_item) {
        totalAmount += Number(item?.approved_sum) || 0
      }
    })
  } else if (bill?.status === "signed") {
    items.forEach((item: any) => {
      if (item?._boq_item?.is_item) {
        totalAmount += Number(item?.paid_sum) || 0
      }
    })
  }

  return totalAmount
}

export const getFinancialErrorMessage = (
  error: any,
  context: string,
  translate: (key: string) => string,
): string => {
  // Log full error for debugging (visible in browser DevTools → Console)
  if (error?.response) {
    console.warn(`[PMA Financial Error] ${context}:`, {
      status: error.response.status,
      data: error.response.data,
      url: error.config?.url,
    })
  }
  const serverMessage = error?.response?.data?.message || error?.response?.data?.payload?.message
  if (serverMessage) {
    const translated = translate(serverMessage)
    return translated !== serverMessage.toLowerCase() ? translated : serverMessage
  }
  const statusCode = error?.response?.status
  if (statusCode === 401 || statusCode === 403) return translate("you have not access")
  if (statusCode === 404) return translate("not found")
  return translate(`failed to ${context}. please try again.`)
}

export const getDestinationTab = (action: string): string => {
  const tabMap: Record<string, string> = {
    approve: "for_sign",
    sign: "signed",
    return_to_contractor: "contractor",
    return_to_supervisor: "unsigned",
    decline: "declined",
    create: "all",
    edit: "all",
  }
  return tabMap[action] || "all"
}

export function utilizationColor(pct: number): string {
  if (pct >= 90) return "#ef4444"
  if (pct >= 70) return "#f59e0b"
  if (pct >= 50) return "#22c55e"
  return "#3b82f6"
}

/**
 * Classify a bill's status from its boolean fields.
 * The API returns a `status` string, but we also check booleans for reliability.
 */
export function classifyBillStatus(bill: any): string {
  const apiStatus = (bill?.status || "").toLowerCase()
  if (apiStatus) return apiStatus
  if (bill?.signed) return "signed"
  if (bill?.decline) return "declined"
  if (bill?.returned) return "returned"
  if (bill?.approved_sum > 0) return "approved"
  return "pending"
}

export function getChartColor(index: number): string {
  return CHART_PALETTE[index % CHART_PALETTE.length]
}

/**
 * Build a dynamic color+label map from the actual bill statuses found in data.
 * No hardcoded map — colors assigned from palette, labels from translate().
 */
export function buildBillStatusMap(
  bills: any[],
  translate: (key: string) => string,
): Map<string, BillStatusInfo> {
  const seen = new Map<string, BillStatusInfo>()
  let colorIdx = 0
  bills.forEach((bill) => {
    const key = classifyBillStatus(bill)
    if (!seen.has(key)) {
      seen.set(key, {
        key,
        label: translate(key) || key.charAt(0).toUpperCase() + key.slice(1),
        color: getChartColor(colorIdx++),
      })
    }
  })
  return seen
}

// ---------------------------------------------------------------------------
// Unified Drill-Down Title / Subtitle Resolver
// ---------------------------------------------------------------------------
// Consolidates the 4 per-module getDrillDownTitle/getDrillDownSubtitle helpers
// (tasks, financial, work-diary, files) into a single resolver that handles
// every DrillDownState type.

const DRILL_DOWN_TITLE_MAP: Record<
  string,
  (drillDown: any, translate: (k: string) => string, extra?: any) => string
> = {
  // Tasks
  kpi: (d, t) =>
    d.category === "all"
      ? t("My Tasks")
      : d.category === "open"
        ? t("Open Tasks")
        : d.category === "completed"
          ? t("Completed Tasks")
          : t("Canceled Tasks"),
  status: (d) => d.statusName,
  project: (d) => d.projectName,
  assignee: (d) => d.userName,
  "overdue-projects": (_d, t) => t("Overdue Projects"),
  "at-risk-projects": (_d, t) => t("At Risk Projects"),
  date: (d) => d.date,
  // Financial
  boq: (d) => d.projectName,
  "bill-status": (d, t) =>
    d.statusKey === "__utilization__" ? t("Budget Utilization") : t("Bills"),
  // Work Diary
  "diary-project": (d) => d.projectName,
  "diary-approval": (d, t) =>
    d.approvalState === "approved"
      ? t("Approved")
      : d.approvalState === "pending"
        ? t("Pending")
        : t("Rejected"),
  // Files
  "file-type": (d, t) => `${d.extension.toUpperCase()} ${t("Files")}`,
  "file-project": (d) => d.projectName,
}

const DRILL_DOWN_SUBTITLE_MAP: Record<
  string,
  (drillDown: any, translate: (k: string) => string) => string
> = {
  // Tasks
  kpi: (_d, t) => t("Tasks filtered by category"),
  status: (_d, t) => t("Tasks with this status"),
  project: (_d, t) => t("All tasks in this project"),
  assignee: (_d, t) => t("Tasks assigned to this person"),
  "overdue-projects": (_d, t) => t("Projects past their due date"),
  "at-risk-projects": (_d, t) => t("Projects below 30% completion"),
  date: (_d, t) => t("Tasks activity on this date"),
  // Financial
  boq: (_d, t) => t("BOQ items in this project"),
  "bill-status": (d, t) =>
    d.statusKey === "__utilization__"
      ? t("All BOQs sorted by utilization")
      : t("Bills with this status"),
  // Work Diary
  "diary-project": (_d, t) => t("Diary entries for this project"),
  "diary-approval": (_d, t) => t("Diaries matching this approval state"),
  // Files
  "file-type": (_d, t) => t("All files of this type"),
  "file-project": (_d, t) => t("All files in this project"),
}

export function getDrillDownTitle(
  drillDown: DrillDownState,
  translate: (key: string) => string,
): string {
  if (!drillDown) return ""
  const resolver = DRILL_DOWN_TITLE_MAP[drillDown.type]
  return resolver ? resolver(drillDown, translate) : ""
}

export function getDrillDownSubtitle(
  drillDown: DrillDownState,
  translate: (key: string) => string,
): string {
  if (!drillDown) return ""
  const resolver = DRILL_DOWN_SUBTITLE_MAP[drillDown.type]
  return resolver ? resolver(drillDown, translate) : ""
}

export function saturateForChart(hexColor: string): string {
  const hex = hexColor.replace("#", "")
  if (hex.length !== 6 && hex.length !== 3) return hexColor

  const fullHex = hex.length === 3 ? hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2] : hex

  const r = parseInt(fullHex.slice(0, 2), 16) / 255
  const g = parseInt(fullHex.slice(2, 4), 16) / 255
  const b = parseInt(fullHex.slice(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2

  // Only adjust light/pastel colors (lightness > 0.65)
  if (l <= 0.65) return hexColor

  let h = 0
  let s = 0
  const d = max - min

  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6
    else if (max === g) h = ((b - r) / d + 2) / 6
    else h = ((r - g) / d + 4) / 6
  }

  // Increase saturation and decrease lightness
  const newS = Math.min(1, s + 0.3)
  const newL = Math.max(0.3, l - 0.25)

  // HSL to RGB
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }

  const q = newL < 0.5 ? newL * (1 + newS) : newL + newS - newL * newS
  const p = 2 * newL - q
  const nr = Math.round(hue2rgb(p, q, h + 1 / 3) * 255)
  const ng = Math.round(hue2rgb(p, q, h) * 255)
  const nb = Math.round(hue2rgb(p, q, h - 1 / 3) * 255)

  return `#${nr.toString(16).padStart(2, "0")}${ng.toString(16).padStart(2, "0")}${nb.toString(16).padStart(2, "0")}`
}

export const getModifiedRangePayload = (
  filter: string,
  dateRange: DateRange | undefined,
): { from: number; to: number } | null => {
  if (!filter) return null

  if (filter === "custom_range") {
    if (!dateRange?.from) return null
    const to = dateRange.to ?? dateRange.from
    return { from: dateRange.from.getTime(), to: to.getTime() }
  }

  const now = Date.now()
  const startOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)
  const startMs = startOfToday.getTime()
  const dayMs = 24 * 60 * 60 * 1000

  switch (filter) {
    case "today":
      return { from: startMs, to: now }
    case "last_7_days":
      return { from: startMs - 7 * dayMs, to: now }
    case "last_30_days":
      return { from: startMs - 30 * dayMs, to: now }
    case "this_year":
      return { from: new Date(startOfToday.getFullYear(), 0, 1).getTime(), to: now }
    default:
      return null
  }
}
